import { Skeleton } from "antd";
import { useEffect, useState } from "react";
import { FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { AutoComplete } from "../../../../shared/components/Inputs/AutoComplete";
import Field from "../../../../shared/components/Inputs/Field";
import InputMoney from "../../../../shared/components/Inputs/InputMoney";
import { LoadingPage } from "../../../../shared/components/LoadingPage";
import { useGetAllContatos } from "../../../Contatos/hooks/useGetContatos";
import { useGetAllNotaFiscal } from "../../../NotasFiscais/hooks/useGetNotasFiscais";
import { useGetAllProdutos } from "../../../Produto/hooks/useGetProdutos";
import useGetBotijao from "../../hooks/useGetBotijao";
import useGetCaneca from "../../hooks/useGetCaneca";
import useGetMovimentoTipo from "../../hooks/useGetMovimentoTipo";
import { MovimentoForm } from "../../types";

interface FormMovimentosTypes {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  movimentos?: MovimentoForm;
  disabled?: boolean;
  errors?: FieldErrors<any>;
  formEdit?: boolean;
  control?: any;
  loading?: boolean;
}

interface MovimentoOption {
  value: string;
  label: string
}

export function FormMovimentos({
  register,
  setValue,
  movimentos,
  disabled,
  control,
  loading,
  errors,
  formEdit
}: FormMovimentosTypes) {
  const [notasFiscaisOptions, setNotasFiscaisOptions] = useState<MovimentoOption[]>([]);
  const [movimentoOptions, setMovimentoOptions] = useState<MovimentoOption[]>([]);
  const [contatosOptions, setContatosOptions] = useState<MovimentoOption[]>([]);
  const [produtosOptions, setProdutosOptions] = useState<MovimentoOption[]>([]);
  const [canecaOptions, setCanecaOptions] = useState<MovimentoOption[]>([]);
  const [botijaoOptions, setBotijaoOptions] = useState<MovimentoOption[]>([]);


  const { data: botijaoData } = useGetBotijao();
  const { data: canecaData } = useGetCaneca();
  const { data: produtosData } = useGetAllProdutos();
  const { data: contatosData } = useGetAllContatos();
  const { data: movimentoTipoData } = useGetMovimentoTipo();
  const { data: notasFiscaisData } = useGetAllNotaFiscal();

  useEffect(() => {
    if (movimentoTipoData) {
      const options = movimentoTipoData.map((item: any) => ({
        value: item.MovimentoTipoId,
        label: item.Nome,
      }));
      setMovimentoOptions(options);
    }

    if (notasFiscaisData) {
      const options = notasFiscaisData.map((item: any) => ({
        value: item.NotaFiscalId,
        label: item.Numero.toString(),
      }));
      setNotasFiscaisOptions(options);
    }

    if (contatosData) {
      const options = contatosData.map((item: any) => ({
        value: item.Id,
        label: item.Nome,
      }));
      setContatosOptions(options);
    }

    if (produtosData) {
      const options = produtosData.map((item: any) => ({
        value: item.ProdutoId,
        label: item.Nome,
      }));
      setProdutosOptions(options);
    }

    if (canecaData) {
      const options = canecaData.map((item: any) => ({
        value: item.CanecaId,
        label: item.Nome,
      }));
      setCanecaOptions(options);
    }

    if (botijaoData) {
      const options = botijaoData.map((item: any) => ({
        value: item.BotijaoId,
        label: item.Nome,
      }));
      setBotijaoOptions(options);
    }
  }, [notasFiscaisData, movimentoTipoData, contatosData, produtosData, canecaData, botijaoData]);

  return (
    <form className="w-[70rem]">
      {notasFiscaisOptions.length === 0 ? <div className="h-[21.5rem] flex items-center justify-center"><LoadingPage /></div> : <>
        <div className="flex justify-center items-center gap-3 w-[100%]">
          <label htmlFor="MovimentoTipoId" className="h-[5rem]">
            Selecione o tipo de movimento<span className="text-error">*</span>
            {loading ? <Skeleton.Input active={true} size="default" style={InputLargeLoading} /> :
              <AutoComplete
                dataOptions={movimentoOptions}
                disabled={disabled}
                dataName="MovimentoTipoId"
                control={control}
                rules={{ required: true }}
              />
            }
            {errors?.MovimentoTipoId && <span className="text-error">Campo obrigatório</span>}
          </label>

          <label htmlFor="NotaFiscalId" className="h-[5rem]">
            Selecione a nota fiscal
            {loading ? <Skeleton.Input active={true} size="default" style={InputLargeLoading} /> :
              <AutoComplete
                dataOptions={notasFiscaisOptions}
                disabled={disabled}
                dataName="NotaFiscalId"
                control={control}
              />
            }
          </label>

          <label htmlFor="CodigoReferencia">
            Codigo de referência
            {loading ? <Skeleton.Input active={true} size="default" style={InputMediumLoading} /> :
              <input
                type="text"
                {...register("CodigoReferencia")}
                name="CodigoReferencia"
                disabled={disabled}
                className="input-login-style w-[11rem] mb-5 disabled:text-[#B6B5B6] disabled:bg-[#F3F3F3]"
              />
            }
          </label>

          <label htmlFor="DataMovimento">
            Data de movimento<span className="text-error">*</span>
            {loading ? <Skeleton.Input active={true} size="default" style={InputMediumLoading} /> :
              <Field
                control={control}
                name="DataMovimento"
                rules={{ required: true }}
                render={({ field }) => (
                  <input
                    type="date"
                    className={"input-login-style w-[11rem] mb-5 disabled:text-[#B6B5B6] disabled:bg-[#F3F3F3]"}
                    disabled={disabled}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />}
          </label>
        </div>

        <div className="flex gap-3 justify-center items-center">
          <label htmlFor="ContatoId" className="h-[5rem]">
            Selecione o contato<span className="text-error">*</span>
            {loading ? <Skeleton.Input active={true} size="default" style={InputLargeLoading} /> :
              <AutoComplete
                dataOptions={contatosOptions}
                disabled={disabled}
                dataName="ContatoId"
                control={control}
                rules={{ required: true }}
              />
            }
            {errors?.ContatoId && <span className="text-error">Campo obrigatório</span>}
          </label>

          <label htmlFor="ProdutoId" className="h-[5rem]">
            Selecione o produto<span className="text-error">*</span>
            {loading ? <Skeleton.Input active={true} size="default" style={InputLargeLoading} /> :
              <AutoComplete
                dataOptions={produtosOptions}
                disabled={disabled}
                dataName="ProdutoId"
                control={control}
                rules={{ required: true }}
              />
            }
            {errors?.ProdutoId && <span className="text-error">Campo obrigatório</span>}
          </label>

          <label htmlFor="Quantidade">
            Quantidade<span className="text-error">*</span>
            {loading ? <Skeleton.Input active={true} size="small" style={InputSmallLoading} /> :
              <input
                type="number"
                disabled={disabled}
                {...register("Quantidade", { required: true })}
                name="Quantidade"
                onKeyPress={function(e) {
                  if(e.key === "," || e.key === ".") {
                    e.preventDefault();
                  }
                }}
                className={`input-login-style w-[7.1rem] ${!errors?.Quantidade && 'mb-5'} text-right disabled:text-[#B6B5B6] disabled:bg-[#F3F3F3]`}
              />
            }
            {errors?.Quantidade?.type === 'required' && <span className="text-error">Campo obrigatório</span>}
          </label>

          <label htmlFor="ValorUnitarioReais">
            Valor unitário
            {loading ? <Skeleton.Input active={true} size="small" style={InputSmallLoading} /> :
              <Field
                control={control}
                rules={{ required: false }}
                name="ValorUnitarioReais"
                render={({ field }) => (
                  <InputMoney
                    disabled={disabled}
                    value={field.value}
                    onChange={field.onChange}
                    className="input-login-style w-[7.1rem] mb-5 disabled:text-[#B6B5B6] disabled:bg-[#F3F3F3]"
                  />
                )}
              />
            }
          </label>
          <label htmlFor="DescontoPorcentagem">
            Desconto (%)
            {loading ? <Skeleton.Input active={true} size="small" style={InputSmallLoading} /> :
              <input
                disabled={disabled}
                type="number"
                {...register("DescontoPorcentagem")}
                name="DescontoPorcentagem"
                maxLength={3}
                className={`input-login-style w-[7.1rem] mb-5 text-right disabled:text-[#B6B5B6] disabled:bg-[#F3F3F3]`}
              />
            }
          </label>
        </div>

        <div className="flex gap-3 justify-center items-center">
        <label htmlFor="BotijaoId" className="h-[5rem]">
            Selecione o botijão
            {loading ? <Skeleton.Input active={true} size="default" style={InputLargeLoading} /> :
              <AutoComplete
                dataOptions={botijaoOptions}
                disabled={disabled}
                dataName="BotijaoId"
                control={control}
              />
            }
          </label>

          <label htmlFor="CanecaId" className="h-[5rem]">
            Selecione a caneca
            {loading ? <Skeleton.Input active={true} size="default" style={InputLargeLoading} /> :
              <AutoComplete
                dataOptions={canecaOptions}
                disabled={disabled}
                dataName="CanecaId"
                control={control}
              />
            }
          </label>

          <label htmlFor="RetiranteContatoId" className="h-[5rem]">
            Selecione o retirante
            {loading ? <Skeleton.Input active={true} size="default" style={InputLargeLoading} /> :
              <AutoComplete
                dataOptions={contatosOptions}
                disabled={disabled}
                dataName="RetiranteContatoId"
                control={control}
              />
            }
          </label>

        </div>

        <div className="flex gap-3 justify-center items-center">
          
          <label htmlFor="Observacoes">
            Observações
            {loading ? <Skeleton.Input active={true} size="default" style={InputObersavacaoLoading} /> :
              <input
                disabled={disabled}
                type="text"
                {...register("Observacoes")}
                name="Observacoes"
                className="input-login-style w-[70rem] mb-5 disabled:text-[#B6B5B6] disabled:bg-[#F3F3F3]"
              />
            }
          </label>
        </div>
        <span className="text-error">Campos com * são obrigatórios</span>
      </>}
    </form>
  );
}

const InputSmallLoading = {
  width: '7.1rem',
  height: '2.5rem',
  marginBottom: '20px'
}

const InputMediumLoading = {
  width: '11rem',
  height: '2.5rem',
  marginBottom: '20px'
}

const InputLargeLoading = {
  width: '22.9rem',
  height: '2.5rem',
  marginBottom: '20px',
}

const InputObersavacaoLoading = {
  width: '70rem',
  height: '2.5rem',
  marginBottom: '20px',
}
